/**
 * Prevents Radix UI / react-remove-scroll from causing a horizontal layout
 * shift when a Select / Popover / Dialog opens.
 *
 * Root cause:
 *   react-remove-scroll (used internally by Radix) sets
 *   `body[data-scroll-locked]` and injects a stylesheet that applies
 *   `margin-right: <scrollbar-width>px !important` to body to compensate
 *   for the scrollbar it removes via `overflow: hidden`.
 *
 *   Combined with our global `scrollbar-gutter: stable` on <html>, the
 *   scrollbar space is already reserved, so RRS's compensating margin
 *   causes content to jump LEFT (and snap back RIGHT on close).
 *
 * Fix:
 *   Intercept `body.setAttribute('data-scroll-locked', ...)` and swallow
 *   it. Without that attribute RRS's stylesheet selector
 *   `body[data-scroll-locked] { ... }` simply doesn't match — no margin,
 *   no shift. The <style> tag is still inserted but has no effect.
 *
 *   Safe because Radix uses the Portal/overlay for pointer/keyboard
 *   dismissal, and blocking background scroll isn't critical for a
 *   filter dropdown or popover. For Dialog/Modal users who do want
 *   scroll lock, apply `overflow: hidden` via your own styling on a
 *   per-component basis.
 */
export function installScrollLockShim() {
  if (typeof document === 'undefined') return;
  const body = document.body;
  if (!body || body.__scrollLockShimInstalled) return;

  const originalSetAttribute = body.setAttribute.bind(body);
  const originalRemoveAttribute = body.removeAttribute.bind(body);

  body.setAttribute = function (name, value) {
    if (name === 'data-scroll-locked') return;
    return originalSetAttribute(name, value);
  };
  body.removeAttribute = function (name) {
    if (name === 'data-scroll-locked') return;
    return originalRemoveAttribute(name);
  };

  body.__scrollLockShimInstalled = true;
}
